import { EditOutlined } from '@ant-design/icons';
import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { ButtonDeleteConfirm } from '@framework/ui/components/button/ButtonDeleteConfirm.tsx';
import { Button, Space } from 'antd';
import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SignatureBookDetailModalButton } from '#features/packages/common-web/src/ui/communication/detail/signature/SignatureBookDetailModalButton.tsx';
import { useCommunicationsContext } from './context/CommunicationsContext.tsx';
import { CommunicationActorsView } from './detail/CommunicationActorsView.tsx';
import { CommunicationAttachments } from './detail/CommunicationAttachments.tsx';
import { CommunicationCollapseHeader } from './detail/CommunicationCollapseHeader.tsx';
import { CommunicationContainer } from './detail/CommunicationContainer.tsx';
import { CommunicationForwardView } from './detail/CommunicationForwardView.tsx';
import { CommunicationReadStateTag } from './detail/CommunicationReadStateTag.tsx';
import { CommunicationSenderProtocol } from './detail/CommunicationSenderProtocol.tsx';
import { MarkCommunicationAsReadButton } from './detail/MarkCommunicationAsReadButton.tsx';
import { UserAvatar } from './detail/UserAvatar.tsx';
import { getIsCommunicationCurrentUser } from './getIsCommunicationCurrentUser.tsx';
import { CommunicationLogic } from './logic/CommunicationLogic.ts';
import { ForwardCommunicationButton } from './table/components/ForwardCommunicationButton.tsx';

interface CommunicationItemProps {
  communication?: ComunicazioneDTO | null;
  /**
   * Indica se l'utente corrente è il mittente della comunicazione
   * @deprecated
   */
  isCurrentUser?: boolean;
  isPreview?: boolean;
  isBackoffice: boolean | null | undefined;
}

/**
 * Singola comunicazione
 */
export function CommunicationItem(props: CommunicationItemProps) {
  const { communication, isPreview, isBackoffice } = props;
  const { t } = useTranslation();
  const {
    procedureId,
    parentProcedureId,
    isProcedureOpen,
    isEditable,
    onDelete,
    onEdit,
    taskId,
    submissionId,
  } = useCommunicationsContext();
  const [isDeleting, setIsDeleting] = useState(false);
  const user = useCurrentUser();

  if (!communication) return null;

  const sender = CommunicationLogic.sender(communication);
  const isDraft = CommunicationLogic.isDraft(communication);
  const isManual = !!communication.flCaricataManualmente;
  const currentRecipient = CommunicationLogic.currentRecipient(communication, {
    isBackoffice,
    procedureId,
  });

  // TODO Verificare funzione per riconoscere se l'utente corrente è il mittente
  const isCurrentUser = getIsCommunicationCurrentUser(
    sender?.id,
    user?.utenteLoggato,
  );

  const canBeEdited = CommunicationLogic.canBeEdited({
    isDraft,
    isEditable,
    isManual,
    isProcedureOpen,
    procedureId,
    parentProcedureId,
  });

  const avatar = !isPreview && (
    <UserAvatar isCurrentUser={isCurrentUser} name={sender?.denominazione} />
  );

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    onEdit?.(communication);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDeleteDraft = async () => {
    setIsDeleting(true);
    await onDelete?.(communication.id!);
    setIsDeleting(false);
  };

  return (
    <CommunicationContainer
      isPreview={isPreview}
      isDraft={isDraft}
      avatar={avatar}
      isCurrentUser={isCurrentUser}
      header={
        <CommunicationCollapseHeader
          isDraft={isDraft}
          isManual={isManual}
          date={communication.dataInvio}
          subject={communication.oggetto}
          protocol={
            <CommunicationSenderProtocol communication={communication} />
          }
          readState={
            // Lo stato di lettura riguarda solo le comunicazioni ricevute
            !isDraft &&
            !isCurrentUser && (
              <CommunicationReadStateTag recipient={currentRecipient} />
            )
          }
          actors={
            !isPreview && (
              <CommunicationActorsView communication={communication} />
            )
          }
          actions={
            // Se non ci sono taskId né submissionId, siamo in una procedure,
            // dove non ci sono le azioni
            <Space>
              {isBackoffice && (
                <SignatureBookDetailModalButton
                  communication={communication}
                  sender={sender}
                />
              )}
              {(taskId ?? submissionId) && canBeEdited && (
                <>
                  <Button
                    icon={<EditOutlined />}
                    size={'middle'}
                    type={'primary'}
                    onClick={handleOpen}
                  >
                    {isManual
                      ? t('communication-item.edit', 'Modifica')
                      : t(
                          'communication-item.edit-and-send',
                          'Modifica e Invia',
                        )}
                  </Button>
                  <ButtonDeleteConfirm
                    size="middle"
                    iconOnlyButton
                    buttonType="default"
                    buttonDanger
                    loading={isDeleting}
                    onConfirm={() => {
                      handleDeleteDraft();
                    }}
                  />
                </>
              )}
              <MarkCommunicationAsReadButton
                communication={communication}
                recipient={currentRecipient}
                isCurrentUserSender={isCurrentUser}
                isDraft={isDraft}
              />
              <ForwardCommunicationButton
                communication={communication}
                isDraft={isDraft}
                isBackoffice={isBackoffice}
                onEdit={onEdit}
                type="default"
              />
            </Space>
          }
        />
      }
      subject={communication.oggetto}
      message={communication.messaggio}
      attachments={
        <CommunicationAttachments attachments={communication.allegati} />
      }
      forward={<CommunicationForwardView forward={communication.inoltro} />}
    />
  );
}
